/**
 * SHA-256, streamed, off the main thread.
 *
 * WHY THIS FILE EXISTS
 *   Lattice addresses content by hash. The client therefore has to know a
 *   file's SHA-256 *before* it asks the server whether the upload is needed at
 *   all — that question is what makes re-uploading an unchanged 300 MB
 *   assembly cost zero bytes and finish in a blink. So hashing sits on the
 *   critical path of every upload, and it must not cost a dropped frame.
 *
 *   Two constraints shape everything below:
 *     1. `crypto.subtle.digest` is one-shot. It wants the entire file in
 *        memory as one buffer. A folder of SolidWorks assemblies would have us
 *        allocating gigabytes. So we need an INCREMENTAL hash, and the
 *        platform does not offer one.
 *     2. Hashing a gigabyte is ~10 seconds of arithmetic. On the main thread
 *        that is ten seconds of frozen interface. So it runs in a worker.
 *
 *   The implementation is FIPS 180-4 §6.2, written out longhand. It is eighty
 *   lines, it is fully specified, it never changes, and it is verified against
 *   `crypto.subtle.digest` in the test suite — a far better trade than a
 *   dependency in the path of every byte the team owns.
 *
 * SAFETY NET
 *   Both blob stores verify the digest as bytes arrive and reject a mismatch,
 *   so a wrong implementation here would fail loudly and immediately rather
 *   than quietly corrupt the workspace.
 *
 * DATA DEFINITIONS
 *
 *   A HashRequest is one of:
 *     - { type: "hash", id, blob }   "hash this, and tell me how it goes"
 *     - { type: "cancel", id }       "stop; nobody wants the answer any more"
 *   Interpretation: a unit of work for this worker. `id` is the caller's
 *   correlation key: several requests may be in flight, and every response
 *   carries the id it belongs to.
 *
 *   A HashResponse is one of:
 *     - { type: "progress", id, bytesHashed, totalBytes }
 *     - { type: "done", id, sha256, totalBytes }
 *     - { type: "error", id, message }
 *     - { type: "cancelled", id }
 *   Interpretation: everything the caller learns about one request. Exactly
 *   one of done / error / cancelled is sent per id, and it is the last message
 *   for that id.
 */

/* ------------------------------------------------------------------ */
/* The digest                                                          */
/* ------------------------------------------------------------------ */

/** The first 32 bits of the fractional parts of the cube roots of the first 64 primes. */
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
])

/** The first 32 bits of the fractional parts of the square roots of the first 8 primes. */
const H0 = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
])

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'))

/**
 * An incremental SHA-256.
 *
 * DATA DEFINITION
 *   A Sha256 is a mutable accumulator in one of two conditions: OPEN, which
 *   accepts more bytes, or SEALED, which has produced its digest and accepts
 *   no more. Interpretation: `update` may be called any number of times with
 *   any chunking — the digest depends only on the concatenation of the bytes,
 *   never on where the chunk boundaries fell. That property is the entire
 *   reason this class exists, and the test suite checks it directly.
 *
 * WHY A MUTABLE OBJECT IN A FUNCTIONAL CODEBASE
 *   A pure fold over 1 GB would allocate a new 64-byte state per block: sixteen
 *   million short-lived objects. This is the one place in Lattice where the
 *   machine wins the argument. The boundary stays pure: `sha256Of` and
 *   `hashBlob` are functions from bytes to a string.
 */
export class Sha256 {
  private readonly h = H0.slice()
  private readonly w = new Uint32Array(64)
  /** The tail of the message that has not yet filled a 64-byte block. */
  private readonly block = new Uint8Array(64)
  private blockLen = 0
  private totalBytes = 0
  /**
   * The message length to report, frozen before `digestHex` pushes the padding
   * block through `update`. Without it `length` would grow by up to 72 bytes at
   * the moment of sealing — which is exactly when a caller asks how big the
   * thing was.
   */
  private messageBytes = 0
  private sealed: string | undefined

  /**
   * Absorb a chunk.
   * update : Uint8Array -> Sha256   (this, so calls chain)
   */
  update(bytes: Uint8Array): this {
    if (this.sealed !== undefined) throw new Error('Sha256: update after digest')
    this.totalBytes += bytes.length

    let offset = 0
    // Top up a partial block first, so the fast path below is always aligned.
    if (this.blockLen > 0) {
      const wanted = Math.min(64 - this.blockLen, bytes.length)
      this.block.set(bytes.subarray(0, wanted), this.blockLen)
      this.blockLen += wanted
      offset = wanted
      if (this.blockLen < 64) return this
      this.compress(this.block, 0)
      this.blockLen = 0
    }

    // Compress straight out of the caller's buffer: no copy, no allocation.
    while (offset + 64 <= bytes.length) {
      this.compress(bytes, offset)
      offset += 64
    }

    if (offset < bytes.length) {
      this.block.set(bytes.subarray(offset), 0)
      this.blockLen = bytes.length - offset
    }
    return this
  }

  /**
   * Seal the accumulator and return the digest as 64 lowercase hex characters.
   * Calling it twice returns the same string; calling `update` after it throws.
   * digestHex : -> String
   */
  digestHex(): string {
    if (this.sealed !== undefined) return this.sealed

    // FIPS 180-4 §5.1.1: append 0x80, then zeros, then the 64-bit big-endian
    // BIT length, so the padded message is a whole number of blocks.
    const total = this.totalBytes
    this.messageBytes = total
    // Split the bit count without ever exceeding 2^53: a file of 2^53 bits is
    // a petabyte, and `total / 2^29` is exact for every size a browser can hold.
    const bitsHigh = Math.floor(total / 0x20000000)
    const bitsLow = (total % 0x20000000) * 8

    const tail = new Uint8Array(this.blockLen < 56 ? 64 - this.blockLen : 128 - this.blockLen)
    tail[0] = 0x80
    writeUint32BE(tail, tail.length - 8, bitsHigh)
    writeUint32BE(tail, tail.length - 4, bitsLow)
    this.update(tail)

    let out = ''
    for (let i = 0; i < 8; i++) {
      const word = this.h[i]
      out += HEX[(word >>> 24) & 0xff] + HEX[(word >>> 16) & 0xff] + HEX[(word >>> 8) & 0xff] + HEX[word & 0xff]
    }
    this.sealed = out
    return out
  }

  /** How many bytes of MESSAGE have been absorbed. Padding never counts. */
  get length(): number { return this.sealed === undefined ? this.totalBytes : this.messageBytes }

  /** One 64-byte block, in place. FIPS 180-4 §6.2.2. */
  private compress(data: Uint8Array, at: number): void {
    const w = this.w
    for (let i = 0; i < 16; i++) {
      const j = at + i * 4
      w[i] = (data[j] << 24) | (data[j + 1] << 16) | (data[j + 2] << 8) | data[j + 3]
    }
    for (let i = 16; i < 64; i++) {
      const x = w[i - 15]
      const y = w[i - 2]
      const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3)
      const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0
    }

    let a = this.h[0], b = this.h[1], c = this.h[2], d = this.h[3]
    let e = this.h[4], f = this.h[5], g = this.h[6], h = this.h[7]

    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7))
      const ch = (e & f) ^ (~e & g)
      const t1 = (h + S1 + ch + K[i] + w[i]) | 0
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10))
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) | 0
      h = g; g = f; f = e; e = (d + t1) | 0
      d = c; c = b; b = a; a = (t1 + t2) | 0
    }

    this.h[0] = (this.h[0] + a) | 0
    this.h[1] = (this.h[1] + b) | 0
    this.h[2] = (this.h[2] + c) | 0
    this.h[3] = (this.h[3] + d) | 0
    this.h[4] = (this.h[4] + e) | 0
    this.h[5] = (this.h[5] + f) | 0
    this.h[6] = (this.h[6] + g) | 0
    this.h[7] = (this.h[7] + h) | 0
  }
}

/** Write a 32-bit big-endian word. writeUint32BE : Uint8Array Number Number -> void */
function writeUint32BE(out: Uint8Array, at: number, value: number): void {
  out[at] = (value >>> 24) & 0xff
  out[at + 1] = (value >>> 16) & 0xff
  out[at + 2] = (value >>> 8) & 0xff
  out[at + 3] = value & 0xff
}

/**
 * The SHA-256 of a buffer already in memory, as 64 lowercase hex characters.
 * sha256Of : Uint8Array -> String
 */
export const sha256Of = (bytes: Uint8Array): string => new Sha256().update(bytes).digestHex()

/* ------------------------------------------------------------------ */
/* Reading a file without holding it                                   */
/* ------------------------------------------------------------------ */

/** Slice size for the fallback reader. Big enough to amortise, small enough to forget. */
const CHUNK_BYTES = 8 * 1024 * 1024

/** Progress is reported at most this often; a hash is not a spectator sport. */
const PROGRESS_INTERVAL_MS = 100

/**
 * Hand the event loop back at least this often, even inside a worker.
 *
 * WHY THIS IS NEEDED (verified in a browser, not guessed)
 *   A Blob's stream hands over chunks that are already in memory, so
 *   `reader.read()` settles in a MICROTASK. Microtask checkpoints do not
 *   dispatch message events — so without this, a worker hashing a gigabyte
 *   never reaches a point where a "cancel" could be delivered, and pressing
 *   stop does nothing until the hash finishes on its own. One macrotask every
 *   16 MB costs about a millisecond per second of hashing and makes cancelling
 *   instant.
 */
const YIELD_EVERY_BYTES = 16 * 1024 * 1024

export interface HashBlobOptions {
  /** Called with the running byte count, throttled. */
  readonly onProgress?: (bytesHashed: number, totalBytes: number) => void
  /** Stop early. Rejects with an AbortError, like every other cancellable call. */
  readonly signal?: AbortSignal
  /**
   * Hand the event loop back between chunks.
   * Only the main-thread fallback needs this; inside a worker there is nothing
   * to be polite to, and yielding would just make hashing slower.
   */
  readonly yieldToHost?: boolean
}

/**
 * Hash a Blob or File by streaming it, never holding more than one chunk.
 * hashBlob : Blob HashBlobOptions -> Promise<String>   (64 hex characters)
 */
export async function hashBlob(blob: Blob, options: HashBlobOptions = {}): Promise<string> {
  const digest = new Sha256()
  const total = blob.size
  let lastReport = 0

  const report = (done: number, force = false): void => {
    if (!options.onProgress) return
    const now = Date.now()
    if (!force && now - lastReport < PROGRESS_INTERVAL_MS) return
    lastReport = now
    options.onProgress(done, total)
  }

  let sinceYield = 0
  const step = async (chunk: Uint8Array): Promise<void> => {
    throwIfAborted(options.signal)
    digest.update(chunk)
    report(digest.length)
    sinceYield += chunk.length
    if (options.yieldToHost || sinceYield >= YIELD_EVERY_BYTES) {
      sinceYield = 0
      await yieldToHost()
      throwIfAborted(options.signal)
    }
  }

  throwIfAborted(options.signal)

  // Preferred path: the platform hands us chunks and owns the buffering.
  if (typeof blob.stream === 'function') {
    const reader = blob.stream().getReader()
    let drained = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        await step(value)
      }
      drained = true
    } finally {
      // Bailing out early — a cancel, or a throw from `step` — leaves the
      // source open, which keeps the blob's bytes pinned until the reader is
      // collected. Cancelling closes it now and releases the lock as it goes.
      if (drained) reader.releaseLock()
      else void reader.cancel().catch(() => undefined)
    }
  } else {
    for (let offset = 0; offset < total; offset += CHUNK_BYTES) {
      const slice = blob.slice(offset, Math.min(offset + CHUNK_BYTES, total))
      await step(new Uint8Array(await slice.arrayBuffer()))
    }
  }

  report(total, true)
  return digest.digestHex()
}

/** Yield to the host so a paint can happen. yieldToHost : -> Promise<void> */
const yieldToHost = (): Promise<void> => new Promise(resolve => setTimeout(resolve, 0))

/** Raise the standard cancellation error if the caller has lost interest. */
function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortError()
}

/**
 * The error every cancelled operation in Lattice rejects with, so callers have
 * exactly one thing to check for (`e.name === 'AbortError'`).
 * abortError : -> Error
 */
export function abortError(): Error {
  const error = new Error('cancelled')
  error.name = 'AbortError'
  return error
}

/* ------------------------------------------------------------------ */
/* The worker protocol                                                 */
/* ------------------------------------------------------------------ */

export type HashRequest =
  | { readonly type: 'hash'; readonly id: string; readonly blob: Blob }
  | { readonly type: 'cancel'; readonly id: string }

export type HashResponse =
  | { readonly type: 'progress'; readonly id: string; readonly bytesHashed: number; readonly totalBytes: number }
  | { readonly type: 'done'; readonly id: string; readonly sha256: string; readonly totalBytes: number }
  | { readonly type: 'error'; readonly id: string; readonly message: string }
  | { readonly type: 'cancelled'; readonly id: string }

/**
 * The slice of a worker global this file uses.
 *
 * Typed structurally rather than as DedicatedWorkerGlobalScope because this
 * module is also imported on the main thread (for the no-worker fallback) and
 * in Node for its tests. Nothing below may depend on a global that only exists
 * in one of those three places.
 */
interface WorkerScope {
  postMessage: (message: HashResponse) => void
  addEventListener: (type: 'message', listener: (event: { data: HashRequest }) => void) => void
}

/** True when this module is executing as a worker rather than on a page. */
const inWorker =
  typeof (globalThis as { document?: unknown }).document === 'undefined' &&
  typeof (globalThis as { postMessage?: unknown }).postMessage === 'function' &&
  typeof (globalThis as { importScripts?: unknown; WorkerGlobalScope?: unknown }).WorkerGlobalScope !== 'undefined'

if (inWorker) installHandler(globalThis as unknown as WorkerScope)

/**
 * Wire the request/response protocol onto a worker global.
 *
 * Exported so a test can drive the protocol with a fake scope, and so the
 * condition above is the only place that decides what a worker *is*.
 * installHandler : WorkerScope -> void
 */
export function installHandler(scope: WorkerScope): void {
  /**
   * The live jobs, so a later "cancel" can reach one.
   *
   * A cancel message can only be delivered while the hashing loop is awaiting
   * its next chunk — which is exactly when the abort will be noticed, one
   * chunk later at the worst.
   */
  const running = new Map<string, AbortController>()

  scope.addEventListener('message', event => {
    const request = event.data
    if (request.type === 'cancel') { running.get(request.id)?.abort(); return }
    void run(request.id, request.blob)
  })

  async function run(id: string, blob: Blob): Promise<void> {
    const control = new AbortController()
    running.set(id, control)
    try {
      const sha256 = await hashBlob(blob, {
        signal: control.signal,
        onProgress: (bytesHashed, totalBytes) =>
          scope.postMessage({ type: 'progress', id, bytesHashed, totalBytes }),
      })
      scope.postMessage({ type: 'done', id, sha256, totalBytes: blob.size })
    } catch (error) {
      if ((error as Error).name === 'AbortError') scope.postMessage({ type: 'cancelled', id })
      else scope.postMessage({ type: 'error', id, message: (error as Error).message || 'hashing failed' })
    } finally {
      running.delete(id)
    }
  }
}
